//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import * as Predicate from 'effect/Predicate';
import * as Schema from 'effect/Schema';

import { Obj, Ref } from '@dxos/echo';
import { Unit, isTruthy } from '@dxos/util';

export const Base = Schema.Struct({
  /**
   * In streaming mode, this is set to `true` when the block is not complete.
   */
  pending: Schema.optional(Schema.Boolean),
});

export type Base = Schema.Schema.Type<typeof Base>;
/**
 * Subclasses of the "user" turn to separate prompts authored by the user vs ones generated by the system.
 */
export const Disposition = Schema.Literal(
  'user', // message written by user
  'synthetic', // synthetic message created by the system
  'cot', // chain-of-thought content in an assistant message
  'version-pin', // marker on a json block that pins an object version
);
export type Disposition = Schema.Schema.Type<typeof Disposition>;

/**
 * Text
 */
export const Text = Schema.TaggedStruct('text', {
  mimeType: Schema.optional(Schema.String),
  text: Schema.String,

  disposition: Schema.optional(Disposition),

  ...Base.fields,
});

export type Text = Schema.Schema.Type<typeof Text>;
/**
 * Represents part of the reasoning carried out by the model to generate a response.
 */
export const Reasoning = Schema.TaggedStruct('reasoning', {
  /**
   * The reasoning content that the model used to return the output.
   */
  reasoningText: Schema.optional(Schema.String),

  /**
   * The content in the reasoning that was encrypted by the model provider for safety reasons.
   */
  redactedText: Schema.optional(Schema.String),

  /**
   * An optional signature which verifies that the reasoning text was generated by the model.
   */
  signature: Schema.optional(Schema.String),

  ...Base.fields,
});

export type Reasoning = Schema.Schema.Type<typeof Reasoning>;
/**
 * Represents a tool call made by the model.
 */
export const ToolCall = Schema.TaggedStruct('toolCall', {
  /**
   * Id unique to this tool call.
   * Set by the model provider.
   */
  toolCallId: Schema.String,

  /**
   * The name of the tool that was called.
   */
  name: Schema.String,

  /**
   * Unparsed input of the tool call.
   * Anthropic models are known to emit empty strings for tools that have not parameters.
   */
  // TODO(dmaretskyi): We might need to be able to reprsent partial json.
  input: Schema.String,

  /**
   * Is the tool executed by the provider.
   */
  providerExecuted: Schema.Boolean,

  /**
   * Key of the Operation that backs this tool call, when the tool is an Operation invocation.
   * Absent for tool calls handled inline by a toolkit handler (e.g. provider-defined tools, MCP tools).
   */
  operationKey: Schema.optional(Schema.String),

  /**
   * Human-readable name of the Operation that backs this tool call.
   * Absent when the tool is not backed by an Operation.
   */
  operationName: Schema.optional(Schema.String),

  /**
   * Phosphor icon identifier (`ph--<name>--<variant>`) of the Operation that backs this tool call.
   * Absent when the tool is not backed by an Operation or the operation has no icon.
   */
  operationIcon: Schema.optional(Schema.String),

  ...Base.fields,
});

export type ToolCall = Schema.Schema.Type<typeof ToolCall>;
export const ToolResult = Schema.TaggedStruct('toolResult', {
  /**
   * Id of the tool call that this result is for.
   * Must match the Id of the preceding {@link ToolCallContentBlock}.
   */
  toolCallId: Schema.String,

  /**
   * The name of the tool that was called.
   */
  name: Schema.String,

  /**
   * The result of the tool call.
   * JSON encoding is preferred.
   * Missing on error.
   */
  result: Schema.optional(
    Schema.Union(
      Schema.String,
      Schema.suspend(() => ContentBlockResult),
    ),
  ),

  // TODO(dmaretskyi): Use discriminated union.
  // result: Schema.Union(
  //   Schema.TaggedStruct('success', {
  //     result: Schema.Unknown,
  //   }),
  //   Schema.TaggedStruct('error', {
  //     error: Schema.Unknown,
  //   }),
  // ),

  error: Schema.optional(Schema.String),

  /**
   * Is the tool executed by the provider.
   */
  providerExecuted: Schema.Boolean,

  ...Base.fields,
});

export type ToolResult = Schema.Schema.Type<typeof ToolResult>;

/**
 * Tool Result as set of content blocks.
 * Useful for returning images and files.
 */
export const ContentBlockResult = Schema.TaggedStruct('~@dxos/types/ContentBlock.ContentBlockResult', {
  content: Schema.Array(
    Schema.Union(
      Text,
      Schema.suspend(() => Image),
      Schema.suspend(() => File),
    ),
  ),
});
export interface ContentBlockResult extends Schema.Schema.Type<typeof ContentBlockResult> {}

export const isContentBlockResult = (result: unknown): result is ContentBlockResult => {
  return Predicate.isTagged('~@dxos/types/ContentBlock.ContentBlockResult')(result);
};

/**
 * Why the model stopped generating a turn. Mirrors the provider-agnostic finish reasons surfaced by
 * `@effect/ai`. `pause` signals a server-tool turn that the provider paused mid-execution and which
 * must be resumed by resending the assistant content verbatim (e.g. Anthropic `pause_turn`).
 */
export const FinishReason = Schema.Literal(
  'stop',
  'length',
  'content-filter',
  'tool-calls',
  'error',
  'pause',
  'other',
  'unknown',
);
export type FinishReason = Schema.Schema.Type<typeof FinishReason>;

/**
 * GPT Summary
 */
export const Stats = Schema.TaggedStruct('stats', {
  mimeType: Schema.optional(Schema.String),
  message: Schema.optional(Schema.String),
  model: Schema.optional(Schema.String),
  usage: Schema.optional(
    // TOOD(burdon): Reuse AI Usage struct?
    Schema.Struct({
      inputTokens: Schema.optional(Schema.Number),
      outputTokens: Schema.optional(Schema.Number),
      totalTokens: Schema.optional(Schema.Number),
    }),
  ),
  toolCalls: Schema.optional(Schema.Number),
  errors: Schema.optional(Schema.Number),
  duration: Schema.optional(Schema.Number).annotations({
    description: 'Duration in ms.',
  }),
  finishReason: Schema.optional(FinishReason),
  ...Base.fields,
});

export type Stats = Schema.Schema.Type<typeof Stats>;
/**
 * Claude-like message
 * ⎿ Done (15 tool uses · 21.5k tokens (→21.1k ←0.4k) · 1m 13.5s)
 */
// TODO(burdon): String builder.
// TODO(burdon): Move to UI (and use translations).
export const createStatsMessage = ({ message, model, usage, toolCalls, duration }: Stats, verbose = false) => {
  const paren = (str: string) => `(${str})`;
  const parts = [
    verbose && model,
    toolCalls && `${toolCalls} tool uses`,
    usage &&
      [
        `${String(Unit.Thousand(usage.totalTokens ?? 0))} tokens`,
        verbose &&
          paren(
            [
              `→${String(Unit.Thousand(usage.inputTokens ?? 0))}`,
              `←${String(Unit.Thousand(usage.outputTokens ?? 0))}`,
            ].join(' '),
          ),
      ]
        .filter(isTruthy)
        .join(' '),
    duration && Unit.Duration(duration),
  ].filter(isTruthy);

  return [message, paren(parts.join(' · '))].filter(isTruthy).join(' ');
};

export const Base64ImageSource = Schema.Struct({
  type: Schema.Literal('base64'),
  mediaType: Schema.String,
  data: Schema.String,
});

export const HttpImageSource = Schema.Struct({
  type: Schema.Literal('http'),
  url: Schema.String,
});

export const ImageSource = Schema.Union(Base64ImageSource, HttpImageSource);

// TODO(dmaretskyi): Combine image and file similar to effect-ai types.

/**
 * Image
 */
export const Image = Schema.TaggedStruct('image', {
  id: Schema.optional(Schema.String),
  source: Schema.optional(ImageSource),

  ...Base.fields,
});

export type Image = Schema.Schema.Type<typeof Image>;
/**
 * File
 */
export const File = Schema.TaggedStruct('file', {
  /**
   * The URL of the file.
   * Data URLs allow for embedding small files directly in the message.
   */
  url: Schema.String,

  /**
   * The name of the file.
   */
  name: Schema.optional(Schema.String),

  /**
   * The MIME type of the file.
   */
  mediaType: Schema.optional(Schema.String),

  ...Base.fields,
});

export type File = Schema.Schema.Type<typeof File>;
/**
 * Reference
 *
 * Non-text content embedded in the message (e.g., files, polls, etc.).
 */
export const Reference = Schema.TaggedStruct('reference', {
  reference: Ref.Ref(Obj.Unknown),

  ...Base.fields,
});

export type Reference = Schema.Schema.Type<typeof Reference>;
/**
 * A noun / proper noun surfaced by the extraction stage that is not (yet) linked to an object.
 */
export const Candidate = Schema.Struct({
  text: Schema.String,
  kind: Schema.Literal('noun', 'proper-noun'),
  start: Schema.Number,
  end: Schema.Number,
  suggested: Schema.optional(Schema.Struct({ typename: Schema.String })),
});

export type Candidate = Schema.Schema.Type<typeof Candidate>;

/**
 * Transcript block.
 */
export const Transcript = Schema.TaggedStruct('transcript', {
  started: Schema.String,
  text: Schema.String,

  /** Corrected text (punctuation / capitalization / cross-batch word repair); renderers prefer this when present. */
  corrected: Schema.optional(Schema.String),

  /** Resolved entity links surfaced by the extraction stage. */
  references: Schema.optional(Schema.mutable(Schema.Array(Ref.Ref(Obj.Unknown)))),

  /** Nouns / proper nouns mentioned in the block that are not (yet) linked to an object. */
  candidates: Schema.optional(Schema.mutable(Schema.Array(Candidate))),

  /** Target-language rendering produced by the translation stage. */
  translation: Schema.optional(Schema.String),

  ...Base.fields,
});

export type Transcript = Schema.Schema.Type<typeof Transcript>;
/**
 * Agent reporting it's current status.
 */
export const Status = Schema.TaggedStruct('status', {
  statusText: Schema.String,

  ...Base.fields,
});

export type Status = Schema.Schema.Type<typeof Status>;
/**
 * Suggestion for a follow-up prompt for the user.
 */
export const Suggestion = Schema.TaggedStruct('suggestion', {
  text: Schema.String,

  ...Base.fields,
});

export type Suggestion = Schema.Schema.Type<typeof Suggestion>;
/**
 * Multiple choice selection.
 * Usually an answer to assistant's question.
 */
// TODO(burdon): Rename Choice.
export const Select = Schema.TaggedStruct('select', {
  options: Schema.mutable(Schema.Array(Schema.String)),

  ...Base.fields,
});

export type Select = Schema.Schema.Type<typeof Select>;
/**
 * Associates artifact (of a specific version) with this conversation.
 * Used to track associated artifacts as well their changes during the conversation.
 */
// TODO(dmaretskyi): What's the relation of this to the reference content block?
const Anchor = Schema.TaggedStruct('anchor', {
  // TODO(dmaretskyi): Consider making this a DXN.
  objectId: Obj.ID,

  // TODO(dmaretskyi): Better type.
  version: Schema.Unknown,

  ...Base.fields,
});

export type Anchor = Schema.Schema.Type<typeof Anchor>;
/**
 * Proposed content to be added to an artifact.
 */
// TODO(dmaretskyi): Consider handling this via a tool call.
//   Though even if it's also a tool call, we probably still want to render the proposal in the chat.
export const Proposal = Schema.TaggedStruct('proposal', {
  text: Schema.String,

  ...Base.fields,
});

export type Proposal = Schema.Schema.Type<typeof Proposal>;
/**
 * Summary of the conversation prior to this point.
 * Used to compress context.
 */
export const Summary = Schema.TaggedStruct('summary', {
  content: Schema.String,

  ...Base.fields,
});

export type Summary = Schema.Schema.Type<typeof Summary>;
/**
 * Model printing info about the list of available tools.
 */
// TODO(dmaretskyi): Deprecate. Allow injecting html for rendering UI components.
export const Toolkit = Schema.TaggedStruct('toolkit', {
  ...Base.fields,
});

export type Toolkit = Schema.Schema.Type<typeof Toolkit>;
/**
 * Request to render a registered UI surface inline in the conversation.
 * Emitted by the model (via the `<surface>` tag) so an agent can render an interactive
 * affordance — e.g. a connector prompt to authenticate a service it needs — instead of
 * failing silently. The `role` selects which surface renders; `data` is passed to it.
 */
export const Surface = Schema.TaggedStruct('surface', {
  /** Logical surface role, e.g. `integration-prompt`. */
  role: Schema.String,

  /** JSON-serializable payload passed to the surface, e.g. `{ service: 'gmail.com' }`. */
  data: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),

  ...Base.fields,
});

export type Surface = Schema.Schema.Type<typeof Surface>;
/**
 * JSON
 * @deprecated Use {@link Text} with mime type of `application/json`.
 */
export const Json = Schema.TaggedStruct('json', {
  disposition: Schema.optional(Disposition),
  data: Schema.String,

  ...Base.fields,
});

export type Json = Schema.Schema.Type<typeof Json>;
export const Any = Schema.Union(
  Anchor,
  File,
  Image,
  Json,
  Proposal,
  Reasoning,
  Reference,
  Select,
  Status,
  Suggestion,
  Stats,
  Surface,
  Text,
  Summary,
  Toolkit,
  ToolCall,
  ToolResult,
  Transcript,
);

export type Any = Schema.Schema.Type<typeof Any>;

export const is =
  <T extends Any['_tag']>(tag: T) =>
  (block: Any): block is Extract<Any, { _tag: T }> => {
    return block._tag === tag;
  };
